import produce from 'immer';
import React, { createContext, useContext, useReducer } from 'react';

const ADD_NOTIFICATION = 'ADD_NOTIFICATION';
const REMOVE_NOTIFICATION = 'REMOVE_NOTIFICATION';

export interface PrimaryAction
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  text: string;
}

interface NotificationData {
  content: React.ReactNode;
  closeable?: boolean;
  color?: string;
  primaryAction?: PrimaryAction;
  message?: string;
  type: NotificationTypes;
  announcementType?: string;
}

export interface Notification {
  id: string;
  key?: string;
  data: NotificationData;
  width?: string;
  primaryAction?: PrimaryAction;
  message?: string;
}

interface AddNotification {
  type: typeof ADD_NOTIFICATION;
  id: string;
  data: NotificationData;
}

interface RemoveNotification {
  type: typeof REMOVE_NOTIFICATION;
  id: string;
}

type ReducerAction = AddNotification | RemoveNotification;

export type NotificationTypes = 'success' | 'warning' | 'danger' | 'custom';

interface State {
  notifications: Notification[];
  addNotification: (notification: Notification) => void;
  removeNotification: (id: string) => void;
}

const initialState: State = {
  notifications: [],
  addNotification: () => undefined,
  removeNotification: () => undefined,
};

const reducerFn = (draft: State, action: ReducerAction) => {
  switch (action.type) {
    case ADD_NOTIFICATION:
      draft.notifications = [
        ...new Set([
          { id: action.id, data: action.data },
          ...draft.notifications,
        ]),
      ];
      break;
    case REMOVE_NOTIFICATION:
      draft.notifications = draft.notifications.filter(
        ({ id }) => id !== action.id,
      );
      break;
    default:
      throw new Error('Unsupported action type dispatched.');
  }
};

export const LegacyNotificationsContext = createContext(initialState);
export const useLegacyNotifications = (): State =>
  useContext(LegacyNotificationsContext);

interface Props {
  children: JSX.Element;
}

export const LegacyGlobalNotificationsProvider = ({
  children,
}: Props): JSX.Element => {
  const reducer = produce(reducerFn);
  const [state, dispatch] = useReducer(reducer, initialState);

  const removeNotification = (id: string) => {
    dispatch({
      type: REMOVE_NOTIFICATION,
      id,
    });
  };

  const addNotification = ({ id, data }: Notification) => {
    if (!id) {
      throw new Error('A unique notification ID is required.');
    }

    if (state.notifications.some((notification) => notification.id === id)) {
      removeNotification(id);
    }

    dispatch({
      type: ADD_NOTIFICATION,
      id,
      data,
    });
  };

  return (
    <LegacyNotificationsContext.Provider
      value={{
        ...state,
        addNotification,
        removeNotification,
      }}
    >
      {children}
    </LegacyNotificationsContext.Provider>
  );
};
